/**
 * Input atom - The foundation input component.
 *
 * Features:
 * - Floating label animation
 * - Visual validation states (error/success)
 * - Icon support (left/right)
 * - Smooth focus transitions
 */
import { forwardRef, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { AlertCircle, CheckCircle2 } from 'lucide-react';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  success?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  variant?: 'default' | 'filled' | 'glass';
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({
    label,
    error,
    hint,
    success = false,
    leftIcon,
    rightIcon,
    variant = 'default',
    className = '',
    id,
    value,
    defaultValue,
    onFocus,
    onBlur,
    ...props
  }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');
    const [isFocused, setIsFocused] = useState(false);
    const hasValue = value !== undefined ? Boolean(value) : Boolean(defaultValue);
    const isFloating = isFocused || hasValue;

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    };

    // Determine state color
    const stateColor = error
      ? 'border-error focus:border-error focus:ring-error/30'
      : success
      ? 'border-success focus:border-success focus:ring-success/30'
      : 'border-border focus:border-primary focus:ring-primary/30';

    const variantStyles = {
      default: 'bg-background',
      filled: 'bg-surface border-transparent focus:bg-background',
      glass: 'glass border-glass-border',
    };

    // Validation icon
    const ValidationIcon = () => {
      if (error) {
        return <AlertCircle size={20} className="text-error animate-shake" />;
      }
      if (success) {
        return <CheckCircle2 size={20} className="text-success animate-scale-in" />;
      }
      return null;
    };

    return (
      <div className="w-full">
        <div className="relative">
          {/* Floating label */}
          {label && (
            <label
              htmlFor={inputId}
              className={`
                absolute left-4 pointer-events-none
                transition-all duration-fast ease-out
                ${leftIcon ? 'left-11' : 'left-4'}
                ${isFloating
                  ? '-top-2.5 text-xs font-medium bg-background px-1 text-primary'
                  : 'top-1/2 -translate-y-1/2 text-base text-text-muted'
                }
                ${error ? 'text-error' : success ? 'text-success' : ''}
              `}
            >
              {label}
            </label>
          )}

          {/* Left icon */}
          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted">
              {leftIcon}
            </div>
          )}

          {/* Input field */}
          <input
            ref={ref}
            id={inputId}
            value={value}
            defaultValue={defaultValue}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className={`
              w-full rounded-2xl border-2 px-4 py-3.5 text-text
              placeholder-transparent
              transition-all duration-150 ease-out
              focus:outline-none focus:ring-2
              ${variantStyles[variant]}
              ${stateColor}
              ${leftIcon ? 'pl-11' : ''}
              ${rightIcon || error || success ? 'pr-11' : ''}
              ${className}
            `}
            placeholder={label || props.placeholder}
            {...props}
          />

          {/* Right icon or validation icon */}
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {(error || success) ? <ValidationIcon /> : rightIcon}
          </div>
        </div>

        {/* Helper text */}
        {error && (
          <p className="mt-1.5 text-sm text-error flex items-center gap-1 animate-fade-in">
            {error}
          </p>
        )}
        {hint && !error && (
          <p className="mt-1.5 text-sm text-text-muted">{hint}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
